import { evaluateCondition, readPath } from "../dsl/expression.js";
import { designUnitScale } from "../dsl/tokens.js";
import { isMixedCategoryPlan } from "./paginate.js";
import { selectBlueprint } from "./select-blueprint.js";
/**
 * Keep packing under the geometric ceiling. Synthesize heroes need more slack
 * than a uniform grid — overshooting paints past the page bottom.
 *
 * Plain lists use a slightly tighter slack so short categories still fill the
 * authored item pocket instead of leaving a half-empty leaf.
 */
const FIT_SLACK = 0.96;
const FIT_SLACK_SYNTHESIZE = 0.88;
const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;
/**
 * Packing-time sample item so `visibleIf` on `item.*` (description, image, …)
 * evaluates the same way render will for a typical dish — otherwise optional
 * fields collapse to height 0 and we over-pack into overflow:hidden.
 */
function packingSample(index) {
    return {
        id: `__pack_${index}`,
        name: 'Sample dish name',
        description: 'Two-line sample description for packing height',
        price: 12,
        image: 'https://example.com/pack.jpg',
        tags: ['v'],
        allergens: [],
        available: true,
        featured: false,
        position: index,
    };
}
function asPackedItems(items) {
    if (typeof items === 'number') {
        const count = Math.max(0, Math.floor(items));
        return Array.from({ length: count }, (_, index) => packingSample(index));
    }
    return items;
}
export function maxFittingItems(blueprint, canvas, tokens, items, scope = {}) {
    const packed = asPackedItems(items);
    const itemCount = packed.length;
    if (itemCount <= 0)
        return 0;
    const capacityCap = blueprint.capacity?.max ?? Number.POSITIVE_INFINITY;
    const constraintCap = blueprint.constraints.maxItems ?? Number.POSITIVE_INFINITY;
    const declaredCap = Math.min(capacityCap, constraintCap);
    const hardCap = Number.isFinite(declaredCap) ? declaredCap : itemCount;
    const regions = findItemRegions(blueprint.root, {
        tokens,
        canvas,
        availableWidth: Math.max(1, canvas.width - canvas.safeArea * 2),
        availableHeight: Math.max(1, canvas.height - canvas.safeArea * 2),
        columns: 1,
        columnGap: 0,
        rowGap: 0,
    }, { ...scope, page: { ...scope.page, items: packed, itemCount } });
    if (regions.length === 0) {
        return Math.max(1, Math.min(itemCount, hardCap));
    }
    let fit = itemCount;
    for (const region of regions) {
        const regionFit = maxItemsForRegion(region, tokens, packed);
        if (Number.isFinite(regionFit))
            fit = Math.min(fit, regionFit);
    }
    fit = Math.min(fit, hardCap, itemCount);
    // Always allow at least one dish — a blank category page is worse than a
    // single overflowing card the author can fix in the constructor.
    return Math.max(1, fit);
}
/** Geometric ceiling for a blueprint ignoring the caller's itemCount. */
export function blueprintItemCapacity(blueprint, canvas, tokens, scope = {}) {
    return maxFittingItems(blueprint, canvas, tokens, 48, scope);
}
/**
 * Split category plans whose chosen layout cannot hold every assigned item,
 * then pull neighbours (and skip blocking dividers) so short sections do not
 * leave a half-empty leaf.
 *
 * Uses the same `select:${pageIndex}` PRNG forks as `renderBook`, and only
 * advances once the blueprint selected for that exact item count fits — so
 * reflow and render cannot disagree on layout.
 */
export function reflowPlansToFit(plans, template, tokens, profiles, prng, blueprintOverride) {
    const diagnostics = [];
    const work = [...plans];
    const history = [];
    let index = 0;
    let allowPull = true;
    while (index < work.length) {
        const plan = work[index];
        if (plan.group === 'category' && plan.items.length === 0) {
            work.splice(index, 1);
            continue;
        }
        if (plan.group !== 'category') {
            const blueprint = selectForPlan(template, plan, profiles, history, index, prng, blueprintOverride);
            if (blueprint)
                history.push(blueprint.variant);
            allowPull = true;
            index += 1;
            continue;
        }
        const blueprint = selectForPlan(template, plan, profiles, history, index, prng, blueprintOverride);
        if (!blueprint) {
            allowPull = true;
            index += 1;
            continue;
        }
        const fit = maxFittingItems(blueprint, template.canvas, tokens, plan.items, scopeForPlan(plan));
        if (fit < plan.items.length) {
            const keepCount = keepCountForSplit(fit, plan.items.length, template);
            diagnostics.push({
                level: 'info',
                code: 'page-overflow',
                message: `Page would overflow with ${plan.items.length} items in "${blueprint.label}"; kept ${keepCount} and continued on the next page`,
                pageIndex: index,
            });
            const kept = plan.items.slice(0, keepCount);
            const rest = plan.items.slice(keepCount);
            work[index] = { ...plan, items: kept };
            work.splice(index + 1, 0, {
                ...plan,
                items: rest,
                title: undefined,
            });
            // Re-select for the reduced page — do not commit history yet.
            continue;
        }
        // Pull trailing items while this layout still has room. After a pull,
        // re-select once (no further pull) so history matches renderBook and a
        // denser pick can still split instead of clipping.
        if (allowPull && pullForward(work, index, blueprint, template, tokens)) {
            allowPull = false;
            continue;
        }
        history.push(blueprint.variant);
        allowPull = true;
        index += 1;
    }
    return {
        plans: renumberCategoryPages(work.filter((plan) => plan.group !== 'category' || plan.items.length > 0)),
        diagnostics,
    };
}
function selectForPlan(template, plan, profiles, history, index, prng, override) {
    if (override && override.group === plan.group) {
        const forced = template.blueprints.find((blueprint) => blueprint.id === override.blueprintId);
        if (forced)
            return forced;
    }
    const side = sideForIndex(index, template.canvas.spread);
    const selection = selectBlueprint(template, {
        plan,
        profile: plan.category ? profiles[plan.category.id] : undefined,
        side,
        history: history.slice(-Math.max(1, template.flow.varietyWindow)),
        pageIndex: index,
    }, prng.fork(`select:${index}`));
    return selection.blueprint ?? closestCapacityBlueprint(template.blueprints, plan);
}
/**
 * Pagination already avoids a 1-dish last page; reflow splits must do the same.
 * If the geometric ceiling would leave a stub remainder, keep fewer on this
 * leaf so both pages stay at least `minOrphanItems`.
 */
function keepCountForSplit(fit, total, template) {
    const minOrphan = Math.max(1, template.flow.minOrphanItems ?? 2);
    const rest = total - fit;
    if (rest > 0 && rest < minOrphan && fit > minOrphan) {
        return Math.max(1, fit - (minOrphan - rest));
    }
    return fit;
}
/**
 * Move dishes from the next category (or continuation) onto this leaf.
 * Returns true when anything moved, so the caller can re-select.
 */
function pullForward(work, index, blueprint, template, tokens) {
    const minOrphan = Math.max(1, template.flow.minOrphanItems ?? 2);
    let pulled = false;
    while (true) {
        const found = findPullableNeighbour(work, index + 1);
        if (!found)
            break;
        const { cursor } = found;
        const next = work[cursor];
        const current = work[index];
        if (!current.category || !next.category)
            break;
        const crossed = next.category.id !== current.category.id;
        const preview = next.items.map((item, moveIndex) => !crossed || moveIndex > 0
            ? item
            : { ...item, sectionHeading: next.category.name });
        const combined = [...current.items, ...preview];
        const room = maxFittingItems(blueprint, template.canvas, tokens, combined, scopeForPlan({ ...current, items: combined }));
        let take = room - current.items.length;
        if (take <= 0)
            break;
        const leftCount = next.items.length - take;
        if (leftCount > 0 && leftCount < minOrphan) {
            const allFit = room >= current.items.length + next.items.length;
            take = allFit ? next.items.length : next.items.length - minOrphan;
            if (take <= 0)
                break;
        }
        const neighbourIndex = dropSkippableDividersBetween(work, index + 1, cursor);
        const neighbour = work[neighbourIndex];
        const moved = preview.slice(0, take);
        const left = neighbour.items.slice(take);
        const nextTitle = crossed
            ? `${current.title ?? current.category.name} · ${neighbour.category.name}`
            : current.title;
        work[index] = {
            ...current,
            items: [...current.items, ...moved],
            title: nextTitle,
        };
        pulled = true;
        if (left.length === 0) {
            work.splice(neighbourIndex, 1);
            continue;
        }
        work[neighbourIndex] = { ...neighbour, items: left, title: undefined };
        break;
    }
    return pulled;
}
/** Next category leaf, looking past soft dividers and list chrome (spotlights). */
function findPullableNeighbour(work, start) {
    let cursor = start;
    while (cursor < work.length) {
        const plan = work[cursor];
        if (plan.group === 'category' && plan.items.length === 0) {
            work.splice(cursor, 1);
            continue;
        }
        if (isSkippableDivider(plan) || isPassthroughChrome(plan)) {
            cursor += 1;
            continue;
        }
        if (plan.group === 'category' && plan.category && plan.items.length > 0) {
            return { cursor };
        }
        return null;
    }
    return null;
}
/** Soft dividers between the leaf and its neighbour are dropped when we pull. */
function dropSkippableDividersBetween(work, fromInclusive, neighbourIndex) {
    let cursor = neighbourIndex;
    for (let index = neighbourIndex - 1; index >= fromInclusive; index -= 1) {
        if (!isSkippableDivider(work[index]))
            continue;
        work.splice(index, 1);
        cursor -= 1;
    }
    return cursor;
}
function isSkippableDivider(plan) {
    return plan.group === 'divider' && !plan.category?.startsNewSection;
}
function isPassthroughChrome(plan) {
    return plan.group === 'spotlight' || plan.group === 'filler';
}
function sideForIndex(index, spread) {
    if (!spread)
        return 'right';
    return index % 2 === 0 ? 'right' : 'left';
}
function closestCapacityBlueprint(blueprints, plan) {
    const sameGroup = blueprints.filter((blueprint) => blueprint.group === plan.group);
    if (sameGroup.length === 0)
        return null;
    return sameGroup.reduce((best, blueprint) => {
        const bestDist = blueprintCapacityDistance(best, plan.items.length);
        const nextDist = blueprintCapacityDistance(blueprint, plan.items.length);
        return nextDist < bestDist ? blueprint : best;
    });
}
function blueprintCapacityDistance(blueprint, itemCount) {
    if (!blueprint.capacity)
        return Number.MAX_SAFE_INTEGER - blueprint.weight;
    return Math.abs(blueprint.capacity.preferred - itemCount);
}
function scopeForPlan(plan) {
    return {
        category: plan.category
            ? {
                ...plan.category,
                itemCount: plan.category.items?.length ?? plan.items.length,
            }
            : undefined,
        page: {
            items: plan.items,
            itemCount: plan.items.length,
            isFirstOfCategory: plan.pageInCategory === 0,
            pageInCategory: plan.pageInCategory,
            pagesInCategory: plan.pagesInCategory,
        },
        item: plan.spotlightItem,
    };
}
function renumberCategoryPages(plans) {
    const counts = new Map();
    for (const plan of plans) {
        if (plan.group !== 'category' || !plan.category)
            continue;
        counts.set(plan.category.id, (counts.get(plan.category.id) ?? 0) + 1);
    }
    const seen = new Map();
    return plans.map((plan) => {
        if (plan.group !== 'category' || !plan.category)
            return plan;
        const index = seen.get(plan.category.id) ?? 0;
        seen.set(plan.category.id, index + 1);
        const mixed = isMixedCategoryPlan(plan);
        return {
            ...plan,
            pageInCategory: index,
            pagesInCategory: counts.get(plan.category.id) ?? 1,
            title: index === 0
                ? (plan.title ?? plan.category.name)
                : mixed
                    ? plan.title
                    : undefined,
        };
    });
}
function findItemRegions(node, ctx, scope) {
    const regions = [];
    walk(node, ctx, regions, scope);
    return regions;
}
function walk(node, ctx, regions, scope) {
    if (!evaluateCondition(node.visibleIf, scope))
        return;
    const framed = applyFrame(node, ctx);
    const local = applyLayoutBox(node, framed);
    const padded = {
        ...local,
        availableWidth: Math.max(1, local.availableWidth - horizontalPadding(node, local.tokens)),
        availableHeight: Math.max(1, local.availableHeight - verticalPadding(node, local.tokens)),
    };
    if (node.kind === 'repeater' && isItemCollection(node.source)) {
        // `limit`d repeaters (hero thumb, featured strip) only show a subset — they
        // must not cap how many dishes the full list region can hold.
        if (node.limit == null) {
            regions.push(regionFromRepeater(node, padded));
        }
        return;
    }
    if (node.kind === 'frame' && node.entity === 'item') {
        const template = frameItemTemplate(node);
        if (template) {
            const gap = padded.rowGap || padded.columnGap;
            regions.push({
                itemTemplate: template,
                columns: padded.columns,
                gap,
                rowGap: gap,
                budgetWidth: padded.availableWidth,
                budgetHeight: padded.availableHeight,
            });
            return;
        }
    }
    if (node.kind === 'grid') {
        const gap = resolveLength(node.gap, padded.tokens);
        const columnGap = resolveLength(node.columnGap ?? node.gap, padded.tokens);
        const rowGap = resolveLength(node.rowGap ?? node.gap, padded.tokens);
        // Conservative columns for fit: fewer columns → taller pages → fewer items.
        // Using maxColumns here packed past the bottom once synthesize picked min.
        const columns = node.synthesize
            ? Math.max(1, node.synthesize.minColumns || node.columns)
            : Math.max(1, node.columns);
        const next = {
            ...padded,
            columns,
            columnGap,
            rowGap,
        };
        const before = regions.length;
        for (const child of node.children)
            walk(child, next, regions, scope);
        if (node.synthesize) {
            for (let index = before; index < regions.length; index += 1) {
                regions[index].synthesize = {
                    minColumns: node.synthesize.minColumns,
                    maxColumns: node.synthesize.maxColumns,
                    heroRatio: node.synthesize.heroRatio,
                    rhythm: node.synthesize.rhythm,
                };
            }
        }
        return;
    }
    if (node.kind === 'stack') {
        const gap = resolveLength(node.gap, padded.tokens);
        // Absolute children (constructor frames) are positioned, not flowed — mirror
        // renderStack so we don't reserve fake column height or split the item
        // budget across a hero thumb and the real list.
        const hasFramedChildren = node.children.some((child) => child.frame);
        if (node.direction === 'column' && !hasFramedChildren) {
            walkColumnStack(node.children, padded, gap, regions, scope);
            return;
        }
        const next = {
            ...padded,
            columnGap: gap,
        };
        for (const child of node.children)
            walk(child, next, regions, scope);
        return;
    }
    if (node.kind === 'group') {
        for (const child of node.children)
            walk(child, padded, regions, scope);
        return;
    }
    if (node.kind === 'frame' || node.kind === 'slot') {
        const children = node.kind === 'frame'
            ? node.children
            : node.defaultChild
                ? [node.defaultChild]
                : [];
        for (const child of children)
            walk(child, padded, regions, scope);
    }
}
/**
 * Column stacks are header + body + footer. The item repeater only owns the
 * height left after fixed siblings — previously every child saw the full page
 * budget, which packed past the bottom.
 */
function walkColumnStack(children, ctx, gap, regions, scope) {
    if (children.length === 0)
        return;
    const visibleChildren = children.filter((child) => evaluateCondition(child.visibleIf, scope));
    const flexibleIndexes = [];
    let reserved = 0;
    visibleChildren.forEach((child, index) => {
        if (containsItemRegion(child))
            flexibleIndexes.push(index);
        else
            reserved += estimateNodeHeight(child, ctx.availableWidth, ctx.tokens, scope);
    });
    const gaps = gap * Math.max(0, visibleChildren.length - 1);
    const flexibleBudget = Math.max(1, ctx.availableHeight - reserved - gaps);
    const perFlexible = flexibleIndexes.length > 0 ? flexibleBudget / flexibleIndexes.length : flexibleBudget;
    visibleChildren.forEach((child, index) => {
        if (flexibleIndexes.includes(index)) {
            walk(child, { ...ctx, availableHeight: perFlexible }, regions, scope);
        }
        else {
            walk(child, ctx, regions, scope);
        }
    });
}
function containsItemRegion(node) {
    if (node.kind === 'repeater' && isItemCollection(node.source))
        return true;
    if (node.kind === 'frame' && node.entity === 'item')
        return true;
    if (node.kind === 'stack' || node.kind === 'group' || node.kind === 'grid' || node.kind === 'frame') {
        return node.children.some(containsItemRegion);
    }
    if (node.kind === 'slot') {
        return node.defaultChild ? containsItemRegion(node.defaultChild) : false;
    }
    return false;
}
function regionFromRepeater(node, ctx) {
    const gap = gapOf(node, ctx);
    return {
        itemTemplate: node.itemTemplate,
        columns: Math.max(1, ctx.columns),
        gap,
        rowGap: ctx.rowGap || gap,
        budgetWidth: Math.max(1, ctx.availableWidth),
        budgetHeight: Math.max(1, ctx.availableHeight),
    };
}
function frameItemTemplate(node) {
    if (node.children.length === 1)
        return node.children[0];
    if (node.children.length === 0)
        return null;
    return {
        id: `${node.id}__fit`,
        kind: 'stack',
        direction: 'column',
        gap: 0,
        children: node.children,
    };
}
function maxItemsForRegion(region, tokens, items) {
    const synth = region.synthesize;
    const cols = Math.max(1, synth ? Math.min(region.columns, synth.minColumns || region.columns) : region.columns);
    const gapsX = (cols - 1) * (region.gap || region.rowGap);
    const cellWidth = Math.max(1, (region.budgetWidth - gapsX) / cols);
    const heroExtra = synth && synth.rhythm !== 'uniform' ? Math.min(0.35, synth.heroRatio * 1.25) : 0;
    const slack = synth ? FIT_SLACK_SYNTHESIZE : FIT_SLACK;
    const budget = region.budgetHeight * slack;
    const rowGap = region.rowGap || region.gap;
    const headingHeight = sectionHeadingHeight(tokens);
    const heights = items.map((item) => estimateNodeHeight(region.itemTemplate, cellWidth, tokens, { item }));
    if (heights.every((height) => !(height > 0)))
        return Number.POSITIVE_INFINITY;
    const cap = Math.min(items.length, 48);
    if (cols <= 1) {
        let used = 0;
        let count = 0;
        for (let index = 0; index < cap; index += 1) {
            let height = heights[index] * (1 + heroExtra);
            if (sectionHeadingOf(items[index]))
                height += headingHeight + rowGap;
            const extra = count > 0 ? rowGap : 0;
            if (used + extra + height > budget && count > 0)
                break;
            used += extra + height;
            count += 1;
        }
        return count;
    }
    return packGrid({
        items: items.slice(0, cap),
        heights: heights.slice(0, cap),
        cols,
        rowGap,
        budget,
        heroExtra,
        headingHeight,
    });
}
function packGrid(input) {
    const { items, heights, cols, rowGap, budget, heroExtra, headingHeight } = input;
    const completed = [];
    let col = 0;
    let rowHeight = 0;
    let count = 0;
    const totalHeight = (rows, current) => {
        const all = current === undefined ? rows : [...rows, current];
        return all.reduce((sum, height, index) => sum + height + (index > 0 ? rowGap : 0), 0);
    };
    const flushRow = () => {
        if (col === 0)
            return true;
        if (totalHeight(completed, rowHeight) > budget && count > col)
            return false;
        completed.push(rowHeight);
        col = 0;
        rowHeight = 0;
        return true;
    };
    for (let index = 0; index < items.length; index += 1) {
        if (sectionHeadingOf(items[index])) {
            if (!flushRow())
                break;
            const withHeading = totalHeight(completed, headingHeight);
            if (withHeading > budget && count > 0)
                break;
            completed.push(headingHeight);
        }
        const cellHeight = heights[index] * (1 + heroExtra);
        if (col === 0) {
            if (totalHeight(completed, cellHeight) > budget && count > 0)
                break;
            rowHeight = cellHeight;
            col = 1;
        }
        else {
            const nextRow = Math.max(rowHeight, cellHeight);
            if (totalHeight(completed, nextRow) > budget && count > 0)
                break;
            rowHeight = nextRow;
            col += 1;
        }
        count += 1;
        if (col >= cols)
            flushRow();
    }
    return count;
}
function sectionHeadingHeight(tokens) {
    const type = tokens.typography.sectionTitle ?? tokens.typography.display ?? tokens.typography.itemName;
    if (!type)
        return 28;
    return type.size * type.lineHeight + 12;
}
function sectionHeadingOf(entry) {
    if (!entry || typeof entry !== 'object')
        return undefined;
    const value = entry.sectionHeading;
    return typeof value === 'string' && value.trim() ? value : undefined;
}
function estimateNodeHeight(node, availableWidth, tokens, scope = {}) {
    if (!evaluateCondition(node.visibleIf, scope))
        return 0;
    const layoutHeight = layoutLength(node.layout?.height, tokens);
    if (layoutHeight !== null)
        return layoutHeight + verticalPadding(node, tokens);
    switch (node.kind) {
        case 'text': {
            const type = tokens.typography[node.typography];
            if (!type)
                return 0;
            const resolved = interpolate(node.source.template, scope);
            const copy = resolved.trim() || interpolate(node.source.fallback ?? '', scope).trim();
            const maxLines = node.maxLines ?? 1;
            if (!copy) {
                // Missing item fields collapse like renderText. Chrome bindings that
                // are empty only because the packing scope is a stub still need height.
                if (/\{\{\s*item\./.test(node.source.template))
                    return 0;
                const lines = Math.max(1, maxLines * 0.7);
                return type.size * type.lineHeight * lines + verticalPadding(node, tokens);
            }
            const charsPerLine = Math.max(8, Math.floor(availableWidth / Math.max(1, type.size * 0.55)));
            const lines = Math.min(maxLines, Math.max(1, Math.ceil(copy.length / charsPerLine)));
            return type.size * type.lineHeight * lines + verticalPadding(node, tokens);
        }
        case 'image': {
            const layoutH = layoutLength(node.layout?.height, tokens);
            if (layoutH !== null)
                return layoutH + verticalPadding(node, tokens);
            const layoutW = layoutLength(node.layout?.width, tokens);
            if (node.aspect && node.aspect > 0) {
                const width = layoutW ?? availableWidth;
                return width / node.aspect + verticalPadding(node, tokens);
            }
            const minH = layoutLength(node.layout?.minHeight, tokens);
            if (minH !== null)
                return minH + verticalPadding(node, tokens);
            if (layoutW !== null)
                return layoutW * 0.65 + verticalPadding(node, tokens);
            return availableWidth * 0.65 + verticalPadding(node, tokens);
        }
        case 'divider': {
            const scale = designUnitScale(tokens);
            const thickness = typeof node.thickness === 'number'
                ? node.thickness * scale
                : resolveLength(node.thickness, tokens) || scale;
            return thickness + verticalPadding(node, tokens) + 4 * scale;
        }
        case 'shape': {
            const h = layoutLength(node.layout?.height, tokens);
            return (h ?? 0) + verticalPadding(node, tokens);
        }
        case 'spacer': {
            if (node.size === undefined)
                return 0;
            return resolveLength(node.size, tokens);
        }
        case 'stack': {
            const pad = verticalPadding(node, tokens);
            const gap = resolveLength(node.gap, tokens);
            const visible = node.children.filter((child) => evaluateCondition(child.visibleIf, scope));
            if (node.direction === 'row') {
                const childHeights = visible.map((child) => estimateNodeHeight(child, availableWidth, tokens, scope));
                return (childHeights.length ? Math.max(...childHeights) : 0) + pad;
            }
            let total = 0;
            visible.forEach((child, index) => {
                total += estimateNodeHeight(child, availableWidth, tokens, scope);
                if (index < visible.length - 1)
                    total += gap;
            });
            return total + pad;
        }
        case 'grid': {
            const cols = Math.max(1, node.columns);
            const cellW = Math.max(1, (availableWidth - (cols - 1) * resolveLength(node.gap, tokens)) / cols);
            const visible = node.children.filter((child) => evaluateCondition(child.visibleIf, scope));
            const childH = visible.map((child) => estimateNodeHeight(child, cellW, tokens, scope));
            const rowH = childH.length ? Math.max(...childH) : 0;
            return rowH + verticalPadding(node, tokens);
        }
        case 'repeater':
            return estimateNodeHeight(node.itemTemplate, availableWidth, tokens, scope);
        case 'frame':
        case 'group': {
            const visible = node.children.filter((child) => evaluateCondition(child.visibleIf, scope));
            if (visible.length === 0)
                return verticalPadding(node, tokens);
            return (visible.reduce((sum, child) => sum + estimateNodeHeight(child, availableWidth, tokens, scope), 0) + verticalPadding(node, tokens));
        }
        case 'slot':
            return node.defaultChild
                ? estimateNodeHeight(node.defaultChild, availableWidth, tokens, scope)
                : 0;
        default:
            return 0;
    }
}
function interpolate(template, scope) {
    return template.replace(PLACEHOLDER, (_match, path) => {
        const value = readPath(scope, path);
        if (value === null || value === undefined)
            return '';
        if (typeof value === 'number')
            return String(value);
        if (typeof value === 'boolean')
            return value ? 'yes' : '';
        if (Array.isArray(value))
            return value.join(', ');
        if (typeof value === 'object')
            return '';
        return String(value);
    });
}
function applyFrame(node, ctx) {
    if (!node.frame)
        return ctx;
    return {
        ...ctx,
        availableWidth: Math.max(1, node.frame.width * ctx.canvas.width),
        availableHeight: Math.max(1, node.frame.height * ctx.canvas.height),
    };
}
function applyLayoutBox(node, ctx) {
    const width = layoutLength(node.layout?.width, ctx.tokens);
    const height = layoutLength(node.layout?.height, ctx.tokens);
    return {
        ...ctx,
        availableWidth: width ?? ctx.availableWidth,
        availableHeight: height ?? ctx.availableHeight,
    };
}
function layoutLength(value, tokens) {
    if (value == null || value === 'auto' || value === 'full')
        return null;
    return resolveLength(value, tokens);
}
function resolveLength(value, tokens) {
    if (value == null)
        return 0;
    if (typeof value === 'number')
        return value * designUnitScale(tokens);
    return tokens.spacing[value.slice(1)] ?? 0;
}
function gapOf(node, ctx) {
    if (node.kind === 'stack')
        return resolveLength(node.gap, ctx.tokens);
    if (node.kind === 'grid')
        return resolveLength(node.gap, ctx.tokens);
    if (node.kind === 'repeater')
        return ctx.rowGap || ctx.columnGap;
    return ctx.rowGap || ctx.columnGap;
}
function verticalPadding(node, tokens) {
    const padding = node.style?.padding;
    if (!padding)
        return 0;
    return resolveLength(padding.top, tokens) + resolveLength(padding.bottom, tokens);
}
function horizontalPadding(node, tokens) {
    const padding = node.style?.padding;
    if (!padding)
        return 0;
    return resolveLength(padding.left, tokens) + resolveLength(padding.right, tokens);
}
function isItemCollection(source) {
    return (source === 'page.items' ||
        source === 'category.items' ||
        source.endsWith('.items'));
}
//# sourceMappingURL=fit-items.js.map